import { UAParser } from 'ua-parser-js';

export enum DetectableOS {
  macOS = 'Mac OS',
  Windows = 'Windows',
  Android = 'Android',
  iOS = 'iOS',
}

export enum MacChipEnum {
  INTEL = 1,
  APPLE_SILICON = 2,
}

export enum WindowsBitEnum {
  BIT_32,
  BIT_64,
}

interface UserAgentDataHighEntropy {
  architecture?: string;
  bitness?: string;
  platform?: string;
}

interface NavigatorWithUAData extends Navigator {
  userAgentData?: {
    platform?: string;
    getHighEntropyValues?: (
      hints: string[],
    ) => Promise<UserAgentDataHighEntropy>;
  };
}

export const getParsedOS = () => {
  const instance = new UAParser(window.navigator.userAgent);
  return instance.getOS();
};

export const isMac = (): boolean => {
  return getParsedOS().name === DetectableOS.macOS;
};

export const isWindows = (): boolean => {
  return getParsedOS().name === DetectableOS.Windows;
};

export const isAndroid = (): boolean => {
  return getParsedOS().name === DetectableOS.Android;
};

export const isIOS = (): boolean => {
  return getParsedOS().name === DetectableOS.iOS;
};

export const isDesktop = (): boolean => {
  return isMac() || isWindows();
};

export const isMobile = (): boolean => {
  return isIOS() || isAndroid();
};

export const isUndetected = (): boolean => {
  return !(isMac() || isWindows() || isAndroid() || isIOS());
};

const getWebglRenderer = (): string | undefined => {
  let renderer: string | undefined;
  const canvas = document.createElement('canvas');
  try {
    const gl =
      canvas.getContext('webgl2') ||
      canvas.getContext('webgl') ||
      canvas.getContext('experimental-webgl');
    if (gl) {
      let debugInfo;
      if ('getExtension' in gl) {
        debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      }
      if (debugInfo && 'getParameter' in gl) {
        renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
      }
    }
  } catch (e) {
    return renderer;
  }
  return renderer;
};

/**
 * Best-effort classification of a Mac's chip from the (possibly masked)
 * WebGL renderer string. Recognizes:
 *   - Any current/future Apple Silicon: "Apple M1", "Apple M2 Pro",
 *     "Apple M3 Max", "Apple M4", "Apple Silicon", and the
 *     ANGLE-wrapped variants exposed by Chromium on macOS.
 *   - The generic "Apple GPU" string returned when the browser masks the
 *     renderer (post Chrome 88 hardening, Safari, etc.) - on a Mac that
 *     value can only come from an Apple GPU, so it is treated as Apple
 *     Silicon.
 *   - Intel / AMD / NVIDIA / Radeon / Iris GPUs as Intel Macs.
 * Returns `undefined` when the renderer is absent or unrecognized so the
 * caller can fall back to a higher-confidence signal or a default.
 */
const classifyMacChipFromRenderer = (
  renderer: string | undefined,
): MacChipEnum | undefined => {
  if (!renderer) {
    return undefined;
  }
  if (/apple\s+(m\d+|silicon)/i.test(renderer)) {
    return MacChipEnum.APPLE_SILICON;
  }
  if (/\bapple\s+gpu\b/i.test(renderer)) {
    return MacChipEnum.APPLE_SILICON;
  }
  if (/\b(intel|amd|nvidia|radeon|iris|geforce)\b/i.test(renderer)) {
    return MacChipEnum.INTEL;
  }
  return undefined;
};

let cachedMacChip: MacChipEnum | undefined;
let macChipPreloadPromise: Promise<MacChipEnum | undefined> | undefined;

/**
 * Test-only helper. Resets the cached Mac-chip detection so each test starts
 * from a clean slate.
 */
export const resetMacChipCacheForTests = () => {
  cachedMacChip = undefined;
  macChipPreloadPromise = undefined;
};

const getNavigatorWithUAData = (): NavigatorWithUAData | undefined => {
  if (typeof navigator === 'undefined') {
    return undefined;
  }
  return navigator as NavigatorWithUAData;
};

/**
 * Asks the browser - via User-Agent Client Hints - whether the current device
 * is ARM64. Available in Chromium-based browsers (Chrome, Edge, Opera, Brave).
 * Resolves to `undefined` when the API is unavailable (Safari, Firefox today)
 * or when the high-entropy request is rejected.
 */
const detectMacChipFromUAClientHints = async (): Promise<
  MacChipEnum | undefined
> => {
  const nav = getNavigatorWithUAData();
  const getHighEntropyValues = nav?.userAgentData?.getHighEntropyValues;
  if (!getHighEntropyValues) {
    return undefined;
  }
  try {
    const values = await getHighEntropyValues.call(nav!.userAgentData, [
      'architecture',
      'bitness',
      'platform',
    ]);
    const platform = (values.platform || nav!.userAgentData!.platform || '')
      .toString()
      .toLowerCase();
    if (platform && platform !== 'macos') {
      return undefined;
    }
    const architecture = (values.architecture || '').toString().toLowerCase();
    if (architecture === 'arm' || architecture === 'arm64') {
      return MacChipEnum.APPLE_SILICON;
    }
    if (architecture === 'x86') {
      return MacChipEnum.INTEL;
    }
    return undefined;
  } catch (e) {
    return undefined;
  }
};

/**
 * Eagerly probes the chip type so the synchronous {@link getMacChip} caller
 * (e.g. the App Download button click handler) can return the
 * highest-confidence value without needing to await anything. Safe to call
 * multiple times; subsequent invocations return the same in-flight promise.
 *
 * Should be invoked once during app bootstrap. No-op on non-Mac platforms.
 */
export const preloadMacChip = (): Promise<MacChipEnum | undefined> => {
  if (!isMac()) {
    return Promise.resolve(undefined);
  }
  if (macChipPreloadPromise) {
    return macChipPreloadPromise;
  }
  macChipPreloadPromise = (async () => {
    const fromUACH = await detectMacChipFromUAClientHints();
    if (fromUACH !== undefined) {
      cachedMacChip = fromUACH;
      return fromUACH;
    }
    const fromWebgl = classifyMacChipFromRenderer(getWebglRenderer());
    if (fromWebgl !== undefined) {
      cachedMacChip = fromWebgl;
      return fromWebgl;
    }
    return undefined;
  })();
  return macChipPreloadPromise;
};

/**
 * Synchronously returns the best-known Mac chip type. Detection priority:
 *   1. Cached value from a previous {@link preloadMacChip} call (UA-CH or
 *      WebGL renderer).
 *   2. A fresh WebGL renderer probe, broadened to recognize M1/M2/M3/M4/...
 *      and the masked "Apple GPU" string.
 *   3. Default: `MacChipEnum.APPLE_SILICON`. Apple stopped selling new Intel
 *      Macs in 2023, so when no signal is available - typically Safari or a
 *      privacy-hardened browser - Apple Silicon is the safer guess and avoids
 *      serving an x86 build to an ARM Mac.
 *
 * Returns `undefined` only when the OS is not macOS.
 */
export const getMacChip = (): MacChipEnum | undefined => {
  if (!isMac()) {
    return undefined;
  }
  if (cachedMacChip !== undefined) {
    return cachedMacChip;
  }
  const fromWebgl = classifyMacChipFromRenderer(getWebglRenderer());
  if (fromWebgl !== undefined) {
    cachedMacChip = fromWebgl;
    return fromWebgl;
  }
  return MacChipEnum.APPLE_SILICON;
};

export const getWindowsBit = () => {
  if (!isWindows()) {
    return;
  }
  const userAgent = navigator.userAgent.toLowerCase();
  const is64Bit =
    userAgent.indexOf('wow64') !== -1 || userAgent.indexOf('win64') !== -1;
  return is64Bit ? WindowsBitEnum.BIT_64 : WindowsBitEnum.BIT_32;
};

export const isSafari = (): boolean => {
  const instance = new UAParser(window.navigator.userAgent);
  return instance.getBrowser().name === 'Safari';
};
